import { ActionMenu, ArrowReplayIcon, DropdownEditor, NumberEditor, TextEditor } from '@approvalmax/ui';
import { Button, Flex } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import { xeroConstants } from 'modules/common/constants';
import AttachmentsDropdown from 'modules/components/Attachment/AttachmentsDropdown';
import { domain } from 'modules/data';
import AmountDueIndicator from 'modules/request/components/xero/AmountDueIndicator';
import { PaymentDetailsIndicator } from 'modules/request/components/xero/PaymentDetailsIndicator/PaymentDetailsIndicator';
import { TbEllipsisColorlessIcon } from 'modules/sprites';
import moment from 'moment';
import { ColumnDefinition } from 'pages/requestList/selectors/types/ColumnDefinition';
import Skeleton from 'react-loading-skeleton';
import { calculateBillAmountDue } from 'shared/helpers';

import { PaymentDetailsDataProvider } from '../../../dataProviders/PaymentDetailsDataProvider/PaymentDetailsDataProvider';
import { columnWidth } from './BillsInformation.constants';
import { messagesConfig as messages } from './BillsInformation.messages';
import { ActionButton, StyledActionMenu } from './BillsInformation.styles';
import { ColumnOptions } from './BillsInformation.types';
import { PaymentDetailsListItem } from './components/PaymentDetailsListItem/PaymentDetailsListItem';

export const getColumnDefinitions = ({
    removeSelectableBillById,
    changeBillInvoiceItemField,
    changePaymentDetails,
    showErrors,
    billInvoiceRequestIdsWithError,
    billInvoiceRequestIdsWithExceededAmount,
    onSelectAttachment,
    openBill,
    activeAttachmentId,
    onOpenAddPaymentDetailsPopup,
    isPaymentDetailsAddEnabled,
    paymentDetailsDiscrepanciesOnChecking,
    isMergedBySupplier,
    amounts,
    isAmountsLoading,
}: ColumnOptions): ColumnDefinition<domain.AmaxPayPaymentItem>[] => {
    const columnDefinition: ColumnDefinition<domain.AmaxPayPaymentItem>[] = [
        {
            id: 'item',
            name: messages.itemName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.friendlyName,
            minWidth: columnWidth.item,
            cell: (value, li) => (
                <Button
                    noUppercase
                    size='small'
                    variant='text'
                    color='blue100'
                    onClick={() => openBill(li.xeroBillInvoiceRequestId)}
                >
                    <Flex spacing='8'>
                        {isMergedBySupplier && <ArrowReplayIcon width={12} height={12} color='midnight60' />}

                        {li.friendlyName}
                    </Flex>
                </Button>
            ),
        },
        {
            id: 'date',
            name: messages.dateName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.date).format('ll'),
            width: columnWidth.date,
        },
        {
            id: 'dueDate',
            name: messages.dueDateName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.dueDate).format('ll'),
            width: columnWidth.dueDate,
        },
        {
            id: 'amountDue',
            name: messages.amountDueName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => {
                const amountDue = calculateBillAmountDue(li, amounts, true);

                return intl.formatNumber(amountDue);
            },
            alignRight: true,
            hideTitle: true,
            width: columnWidth.amountDue,
            cell: (value: string, li) => {
                if (isAmountsLoading) {
                    return <Skeleton />;
                }

                const billAmounts = amounts?.find((amount) => amount.invoiceRequestId === li.xeroBillInvoiceRequestId);

                return (
                    <AmountDueIndicator
                        currency={li.currency}
                        amountOnApproval={billAmounts?.amountOnApproval ?? li.amountOnApproval}
                        amountInProcessing={billAmounts?.amountInProcessing ?? li.amountInProcessing}
                        amountAwaitingPayment={billAmounts?.amountAwaitingPayment ?? li.amountAwaitingPayment}
                        amountOnReview={billAmounts?.amountOnReview ?? li.amountOnReview}
                    >
                        {value}
                    </AmountDueIndicator>
                );
            },
        },
        {
            id: 'billCurrency',
            name: messages.billCurrency,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.currency,
            alignRight: true,
            hideTitle: true,
            alwaysVisible: true,
            width: columnWidth.billCurrency,
        },
        {
            id: 'paymentAmount',
            name: messages.paymentAmount,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => String(li.amount > 0 ? li.amount : 0),
            alwaysVisible: true,
            alignRight: true,
            required: true,
            width: columnWidth.paymentAmount,
            cell: (value: string, li, disabled) => {
                const maxValue = mathService.subtract(
                    li.amountDue,
                    mathService.add(
                        mathService.add(li.amountOnApproval ?? 0, li.amountInProcessing ?? 0),
                        li.amountAwaitingPayment ?? 0
                    )
                );

                return (
                    <NumberEditor
                        value={Number(value)}
                        min={0}
                        align='right'
                        invalid={
                            showErrors &&
                            (!li.amount ||
                                billInvoiceRequestIdsWithExceededAmount.includes(li.xeroBillInvoiceRequestId))
                        }
                        precision={2}
                        disabled={disabled}
                        max={maxValue > 0 ? maxValue : 0}
                        onChange={(val: number) =>
                            changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'amount', val)
                        }
                        changeOnBlur={false}
                    />
                );
            },
        },
        {
            id: 'paymentDetails',
            name: messages.paymentDetails,
            alwaysVisible: true,
            required: true,
            width: columnWidth.paymentDetails,
            value: (li) => li.paymentDetails?.bankAccount.name || '',
            cell: (value, li) => {
                const paymentDetailsDiscrepancy = paymentDetailsDiscrepanciesOnChecking?.find(
                    (paymentDetailsDiscrepancy) =>
                        paymentDetailsDiscrepancy.contactId === li.contact?.id &&
                        paymentDetailsDiscrepancy.paymentDetailsDiscrepancy.paymentDetailsId === li.paymentDetails?.id
                );

                if (isMergedBySupplier && value && li.paymentDetails) {
                    return value;
                }

                return (
                    <PaymentDetailsIndicator
                        forDropDown
                        paymentDetails={li.paymentDetails}
                        counterpartyId={li.contact?.id}
                        status={paymentDetailsDiscrepancy?.paymentDetailsPaymentStatus}
                        paymentDetailsDiscrepancy={paymentDetailsDiscrepancy}
                    >
                        <PaymentDetailsDataProvider counterpartyId={li.contact?.id}>
                            <DropdownEditor
                                invalid={showErrors && !value}
                                value={li.paymentDetails}
                                onChange={(value: domain.PaymentDetails) => {
                                    changePaymentDetails(li.xeroBillInvoiceRequestId, value);
                                }}
                                placeholder={messages.paymentDetailsPlaceholder}
                                listItem={PaymentDetailsListItem}
                                canCreateItemFn={() => !!isPaymentDetailsAddEnabled}
                                createItemDisplayTextFn={() => messages.addPaymentDetails}
                                onCreateItem={() =>
                                    onOpenAddPaymentDetailsPopup({
                                        contactId: li.contact?.id,
                                        contactName: li.contact?.text,
                                        billId: li.xeroBillInvoiceRequestId,
                                    })
                                }
                                displayAttribute='bankAccount.name'
                            />
                        </PaymentDetailsDataProvider>
                    </PaymentDetailsIndicator>
                );
            },
        },
    ];

    if (!isMergedBySupplier) {
        columnDefinition.push({
            id: 'paymentReference',
            name: messages.paymentReference,
            required: true,
            width: columnWidth.paymentReference,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.paymentReference || '',
            alwaysVisible: true,
            cell: (value: string, li: any, disabled) => (
                <TextEditor
                    value={value}
                    maxLength={xeroConstants.amaxPayBatchPaymentReference}
                    disabled={disabled}
                    onChange={(val: string) =>
                        changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'paymentReference', val)
                    }
                    invalid={showErrors && (!value.trim() || value.length > xeroConstants.amaxPayBatchPaymentReference)}
                />
            ),
        });
    }

    columnDefinition.push({
        id: 'attachments',
        name: messages.attachments,
        value: (li: any) => String(li.attachments.length),
        alignRight: true,
        width: columnWidth.attachments,
        cell: (val, li: any) =>
            li.attachments.length > 0 ? (
                <AttachmentsDropdown
                    attachments={li.attachments}
                    activeAttachmentId={activeAttachmentId}
                    onSelectAttachment={(attachmentId: string) =>
                        onSelectAttachment({
                            attachmentId,
                            xeroBatchPaymentInvoiceId: li.xeroBillInvoiceRequestId,
                            xeroBatchPaymentInvoiceName: li.friendlyName,
                        })
                    }
                />
            ) : (
                '—'
            ),
    });
    columnDefinition.push({
        id: 'overflowMenu',
        name: '',
        width: columnWidth.overflowMenu,
        getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
        value: (li) => li.friendlyName,
        cell: (value: string, li) => (
            <StyledActionMenu
                panelFlow='center'
                button={
                    <ActionButton>
                        <Flex alignItems='center'>
                            <TbEllipsisColorlessIcon />
                        </Flex>
                    </ActionButton>
                }
            >
                <ActionMenu.Item
                    execute={() => removeSelectableBillById(li.xeroBillInvoiceRequestId)}
                    title={messages.menuItemRemove}
                >
                    {messages.menuItemRemove}
                </ActionMenu.Item>
            </StyledActionMenu>
        ),
    });

    return columnDefinition;
};
